/**
 * A linha de item com valor e percentual dos cartões do Fechamento.
 *
 * Papel: reproduz a célula que `renderFolhaCard`, `renderFolhaGapCard` e
 * `renderOutrosBancosFechCard` desenham (l. 3442–3443, 3483–3494, 4089–4093):
 * `5px 0`, borda inferior bege, 12 px, o nome à esquerda e, à direita, o valor
 * em `tabular-nums` e o percentual em `texto-fraco` com 32 px de largura mínima.
 * Com subitens, a linha abre e fecha ao clique, com a seta "▶"/"▼", e cada
 * subitem aparece embaixo com `4px 0 4px 20px`, 11 px e `texto-fraco`.
 *
 * Como usar: quem monta passa os textos já escritos — o valor por
 * `formatarReais` e o percentual por `formatarPercentual`. A linha com subitens
 * nasce fechada, como na origem; ela vira um botão, para abrir também pelo
 * teclado.
 */
import { useState } from 'react';

import { cn } from '@/shared/lib/utils';

/** Um subitem: a linha de um código da guia DAE. */
export interface SubitemDaLinha {
  nome: string;
  valor: string;
}

interface LinhaComPercentualProps {
  nome: string;
  /** O valor já escrito. */
  valor: string;
  /** O percentual já escrito, com o "%". */
  percentual: string;
  /** Os subitens; vazio deixa a linha sem seta e sem clique. */
  subitens?: readonly SubitemDaLinha[];
}

/** As classes da linha, igual com e sem subitens. */
const CLASSES_DA_LINHA =
  'flex w-full items-center justify-between gap-2 border-0 border-b border-emb-borda bg-transparent px-0 py-[5px] text-left font-[inherit] text-[12px]';

/**
 * Uma linha de item com percentual.
 *
 * @param nome - O nome do item, à esquerda.
 * @param valor - O valor escrito.
 * @param percentual - O percentual escrito.
 * @param subitens - Os subitens que a linha abre ao clique.
 * @returns A linha e, aberta, os subitens.
 */
export function LinhaComPercentual({
  nome,
  valor,
  percentual,
  subitens = [],
}: LinhaComPercentualProps) {
  const [aberta, setAberta] = useState(false);
  const direita = (
    <span className="flex shrink-0 gap-2 whitespace-nowrap">
      <span className="tabular-nums">{valor}</span>
      <span className="min-w-8 text-right text-texto-fraco">{percentual}</span>
    </span>
  );

  if (subitens.length === 0) {
    return (
      <div className={CLASSES_DA_LINHA}>
        <span className="text-foreground">{nome}</span>
        {direita}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-expanded={aberta}
        onClick={() => setAberta((atual) => !atual)}
        className={cn(CLASSES_DA_LINHA, 'cursor-pointer')}
      >
        <span className="text-foreground">
          <span
            aria-hidden="true"
            className="mr-[5px] inline-block align-middle text-[9px] leading-none text-texto-fraco"
          >
            {aberta ? '▼' : '▶'}
          </span>
          {nome}
        </span>
        {direita}
      </button>
      {aberta && (
        <div>
          {subitens.map((subitem, indice) => (
            <div
              // O mesmo código pode vir duas vezes na guia; a posição desempata.
              key={`${subitem.nome}-${String(indice)}`}
              className="flex justify-between border-b border-emb-borda py-1 pr-0 pl-5 text-[11px] text-texto-fraco"
            >
              <span>{subitem.nome}</span>
              <span className="tabular-nums">{subitem.valor}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
